import { store } from 'redux/store';
import { State } from 'redux/initialState';
import { useDispatch, useSelector } from 'react-redux';
import { FC, createContext, useContext, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useTranslate } from 'hooks/useTranslate';

import { Folder } from 'types';

import actionTypes from 'constants/actionTypes';

import errorSwal from 'utils/errorSwal';
import successSwal from 'utils/successSwal';
import asyncActionSwal from 'utils/asyncActionSwal';

import { setModal } from 'modules/_shared/redux/modalActions';
import { useGetFolders } from 'modules/documents/hooks/useGetFolders';
import { deleteFolder } from 'modules/documents/redux/folderActions';

import { MyFoldersContextType } from '../types/MyFoldersConextType';
import { myFoldersInitValue } from '../constants/myFoldersInitValue';
import { AddFolderModalProvider } from '../../Modals/AddFolderModal/context/AddFolderModalContext';
import AddFolderModal from '../../Modals/AddFolderModal';
import { SortOrder } from '../types/SortOrder';

const MyFoldersContext =
  createContext<MyFoldersContextType>(myFoldersInitValue);

type MyFoldersProviderProps = {
  children: React.ReactNode; // eslint-disable-line
  currentFolder?: Folder;
};

export const MyFoldersProvider: FC<MyFoldersProviderProps> = ({
  children,
  currentFolder,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslate();
  const history = useHistory();

  const { folderId } = useParams();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );

  const folders = useSelector((state: State) => state.folders);

  const [page, setPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(15);
  const [sortOrder, setSortOrder] = useState<SortOrder>(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { isLoading: isLoadingFolders } = useGetFolders({
    societyId: actualSociety?._id,
    currentFolderId: folderId,
    page,
    itemsPerPage,
    sortBy: sortOrder ? 'name' : 'position',
    sortOrder: sortOrder || 'desc',
  });

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };

  const handleAddFolder = () => {};

  const handleOpenFolder = (index: number) => {
    const folder = folders?.[index];
    if (!folder || !actualSociety?._id) return;

    history.push(
      `/documentos/mis-carpetas/${actualSociety?._id}/${folder._id}`
    );
  };

  const handleEditFolder = (index: number) => {
    const folder = folders?.[index];
    if (!folder) return;

    dispatch(
      setModal(
        <AddFolderModalProvider
          currentFolder={folder}
          modalActionType={actionTypes.EDIT}
        >
          <AddFolderModal />
        </AddFolderModalProvider>
      )
    );
  };

  const handleToggleSort = () => {
    setSortOrder((prev) => {
      if (prev === null) return 'asc';
      return prev === 'asc' ? 'desc' : 'asc';
    });
  };

  const handleDownloadFolder = (index: number) => {};

  const handleDeleteFolder = (index: number) => {
    const folder = folders?.[index];
    if (!folder) return;

    asyncActionSwal({
      swalTitle: t('AreYouSureAboutDelFolder'),
      swalElementName: folder.name,
      swalInfoText: t('AllDocumentsInsideWillBeDeleted'),
      swalConfirmButtonText: t('OkDelete'),
      callback: async () => {
        try {
          setIsLoading(true);

          if (folder?._id) {
            await store.dispatch(deleteFolder({ folderId: folder._id }));
            successSwal(t('FolderDeletedSuccessfully'));
          }

          setIsLoading(false);
        } catch (error) {
          setIsLoading(false);
          errorSwal(t('ErrorDeletingFolder'));
        }
      },
    });
  };

  const handleNavigateToFolder = (folderId?: string) => {
    if (!actualSociety?._id) return;

    if (!folderId) {
      return history.push(`/documentos/mis-carpetas/${actualSociety?._id}`);
    }

    history.push(`/documentos/mis-carpetas/${actualSociety?._id}/${folderId}`);
  };

  return (
    <MyFoldersContext.Provider
      value={{
        data: {
          currentFolder,
          foldersCount: currentFolder
            ? currentFolder?.foldersCount?.total
            : actualSociety?.foldersCount?.total || 0,
          documentsCount: currentFolder
            ? currentFolder.documentsCount.total
            : 0,
          sortOrder,
          folders,
          documents: [],
          isLoading: isLoading || isLoadingFolders,
        },
        actions: {
          handleChangePage,
          handleAddFolder,
          handleOpenFolder,
          handleEditFolder,
          handleDownloadFolder,
          handleDeleteFolder,
          handleNavigateToFolder,
          handleToggleSort,
        },
      }}
    >
      {children}
    </MyFoldersContext.Provider>
  );
};

export const useMyFolders = () => {
  const context = useContext(MyFoldersContext);
  if (!context) {
    throw new Error('useMyFolders must be used within a MyFoldersProvider');
  }
  return context;
};
